// src/pages/feedback/components/CommentComposer.tsx
//
// 댓글 입력. 필드가 하나뿐이라 react-hook-form 을 붙이지 않고 로컬 state 로
// 다루되, **길이 상한 메시지는 zod 스키마를 그대로 재사용**한다
// (feedbackCommentFormSchema — 서버 검증과 같은 2,000자).
//
// ⌘/Ctrl+Enter 로 등록할 수 있다. 힌트는 md+ 에서만 보인다(모바일엔
// 물리 키보드가 없다).

import { useState } from "react";
import { Box, Button, CircularProgress, TextField } from "@mui/material";

import { designTokens } from "../../../theme.ts";
import {
  FEEDBACK_LIMITS,
  validateCommentBody,
} from "../../../contracts/feedback/schemas.ts";

const T = designTokens;

const C = {
  sub: "#6B7178",
  caption: "#9AA0A6",
  field: "#F9FAFB",
};

interface CommentComposerProps {
  onSubmit: (body: string) => Promise<void>;
  submitting?: boolean;
}

export default function CommentComposer({
  onSubmit,
  submitting = false,
}: CommentComposerProps) {
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = body.trim().length > 0 && !submitting;

  const submit = async () => {
    const message = validateCommentBody(body);
    if (message) {
      setError(message);
      return;
    }
    try {
      await onSubmit(body.trim());
      setBody("");
      setError(null);
    } catch {
      // 부모가 스낵바로 알린다. 입력은 지우지 않는다 — 실패했다고 사용자가
      // 쓴 글을 날려 버리면 안 된다.
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSubmit) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={handleKeyDown}
        placeholder="댓글을 입력하세요"
        multiline
        minRows={3}
        fullWidth
        disabled={submitting}
        error={Boolean(error)}
        helperText={
          error ??
          `${body.length.toLocaleString("ko-KR")} / ${FEEDBACK_LIMITS.comment.toLocaleString("ko-KR")}`
        }
        inputProps={{ "aria-label": "댓글 입력" }}
        sx={{
          "& .MuiOutlinedInput-root": { bgcolor: C.field, borderRadius: "8px" },
          "& textarea": { fontFamily: T.fonts.sans, fontSize: 14, lineHeight: 1.7 },
          "& .MuiFormHelperText-root": { textAlign: error ? "left" : "right", mx: 0 },
        }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            fontFamily: T.fonts.sans,
            fontSize: 12,
            color: C.caption,
          }}
        >
          ⌘/Ctrl + Enter 로 등록
        </Box>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          fullWidth={false}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          등록
        </Button>
      </Box>
    </Box>
  );
}
